import React, { useState } from "react";
import { toast } from "sonner";
import {
  EnvelopeSimple,
  ShieldCheck,
  ArrowLeft,
  User,
  Phone,
  MapPin,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { requestOtp, verifyOtp } from "@/lib/api";

const emptyForm = { name: "", phone: "", city: "", email: "" };

export const OTPModal = ({ open, onOpenChange, onVerified, reason = "details" }) => {
  const [step, setStep] = useState("form");
  const [form, setForm] = useState(emptyForm);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const COPY = {
    details: { title: "Unlock full show details", desc: "Tell us a bit about you and verify your email. It's free — takes 20 seconds." },
    tickets: { title: "Verify to get tickets", desc: "A quick email verification unlocks tickets & full event details. Free — 20 seconds." },
    inquire: { title: "Verify to inquire", desc: "Share your details and verify your email to connect with the organizer. Free — 20 seconds." },
    upgrade: { title: "Verify to upgrade", desc: "Verify your email first, then unlock premium deal intelligence. Free — 20 seconds." },
    verify: { title: "Verify your email", desc: "Tell us a bit about you and verify your email. It's free — takes 20 seconds." },
    calendar: { title: "Get your free expo calendar", desc: "Verify your email and we'll download the PDF and send a copy to your inbox. Free — 20 seconds." },
  };
  const copy = COPY[reason] || COPY.details;

  const reset = () => {
    setStep("form");
    setCode("");
    setLoading(false);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleRequest = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      return toast.error("Enter a valid phone number");
    if (!form.city.trim()) return toast.error("Please enter your city");
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email))
      return toast.error("Enter a valid email address");

    setLoading(true);
    try {
      const res = await requestOtp({
        email: form.email.trim(),
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
      });
      setStep("otp");
      if (res.dev_code) {
        toast.info(`Dev mode: your code is ${res.dev_code}`, { duration: 8000 });
      } else {
        toast.success(`Verification code sent to ${form.email}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 4) return toast.error("Enter the 4-digit code");
    setLoading(true);
    try {
      const res = await verifyOtp(form.email.trim(), code);
      toast.success("Email verified — welcome aboard!");
      onVerified(res);
      onOpenChange(false);
      setTimeout(() => {
        reset();
        setForm(emptyForm);
      }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setTimeout(reset, 300);
      }}
    >
      <DialogContent
        data-testid="otp-modal"
        className="border-white/10 bg-[#12141F] text-white sm:max-w-md"
      >
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/15 ring-1 ring-blue-500/30">
            {step === "form" ? (
              <EnvelopeSimple size={24} weight="duotone" className="text-blue-400" />
            ) : (
              <ShieldCheck size={24} weight="duotone" className="text-emerald-400" />
            )}
          </div>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">
            {step === "form" ? copy.title : "Enter your code"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === "form" ? copy.desc : `We sent a 4-digit code to ${form.email}.`}
          </DialogDescription>
        </DialogHeader>

        {step === "form" ? (
          <div className="mt-2 space-y-3">
            <Field icon={<User size={16} />}>
              <Input
                data-testid="lead-name-input"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Full name"
                className="h-12 border-white/10 bg-white/[0.04] pl-10 text-white placeholder:text-slate-500 focus-visible:ring-blue-500"
              />
            </Field>
            <Field icon={<Phone size={16} />}>
              <Input
                data-testid="lead-phone-input"
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="Phone number"
                className="h-12 border-white/10 bg-white/[0.04] pl-10 text-white placeholder:text-slate-500 focus-visible:ring-blue-500"
              />
            </Field>
            <Field icon={<MapPin size={16} />}>
              <Input
                data-testid="lead-city-input"
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="City"
                className="h-12 border-white/10 bg-white/[0.04] pl-10 text-white placeholder:text-slate-500 focus-visible:ring-blue-500"
              />
            </Field>
            <Field icon={<EnvelopeSimple size={16} />}>
              <Input
                data-testid="lead-email-input"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleRequest()}
                placeholder="Email address"
                className="h-12 border-white/10 bg-white/[0.04] pl-10 text-white placeholder:text-slate-500 focus-visible:ring-blue-500"
              />
            </Field>
            <Button
              data-testid="lead-submit-btn"
              onClick={handleRequest}
              disabled={loading}
              className="h-12 w-full rounded-full bg-blue-600 font-semibold text-white hover:bg-blue-500"
            >
              {loading ? "Sending…" : "Send verification code"}
            </Button>
            <p className="text-center text-xs text-slate-500">
              We'll only use your details to keep you posted on relevant shows.
            </p>
          </div>
        ) : (
          <div className="mt-2 space-y-5">
            <div className="flex justify-center" data-testid="otp-input-group">
              <InputOTP maxLength={4} value={code} onChange={setCode}>
                <InputOTPGroup className="gap-3">
                  {[0, 1, 2, 3].map((i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      data-testid={`otp-slot-${i}`}
                      className="h-14 w-14 rounded-xl border-white/15 bg-white/[0.04] text-2xl font-bold text-white"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button
              data-testid="otp-verify-btn"
              onClick={handleVerify}
              disabled={loading}
              className="h-12 w-full rounded-full bg-emerald-600 font-semibold text-white hover:bg-emerald-500"
            >
              {loading ? "Verifying…" : "Verify & continue"}
            </Button>
            <button
              data-testid="otp-back-btn"
              onClick={() => setStep("form")}
              className="mx-auto flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white"
            >
              <ArrowLeft size={15} /> Edit my details
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ icon, children }) => (
  <div className="relative">
    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500">{icon}</span>
    {children}
  </div>
);
